const SALE_ORDER_FIELDS = [
  '_id',
  'paid',
  'remainingBalance',
  'returned',
  'invoiceNumber',
  'status',
  'type',
  'modeOfPayment',
  'subTotal',
  'total',
  'createdBy',
  'vatTax',
  'remainingSubtotal',
  'discountType',
  'discount',
  'orderStatus',
] as const;

function buildSoldTo(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as Record<string, unknown>;
  const soldTo: Record<string, unknown> = {};
  if (s.name != null && String(s.name).trim() !== '') soldTo.name = String(s.name);
  if (s.tagNumber != null && String(s.tagNumber).trim() !== '') soldTo.tagNumber = String(s.tagNumber);
  if (s.employeeCode != null && String(s.employeeCode).trim() !== '') soldTo.employeeCode = String(s.employeeCode);
  if (s._id != null && String(s._id).trim() !== '') soldTo._id = String(s._id);
  if (s.type != null && String(s.type).trim() !== '') soldTo.type = String(s.type);
  return Object.keys(soldTo).length ? soldTo : undefined;
}

function buildPayLater(raw: unknown): boolean | undefined {
  if (raw === true) return true;
  if (!raw || typeof raw !== 'object') return undefined;
  return true;
}

/** Open unpaid Pay Later — exclude from reports until payment is collected. */
export function isOpenPayLaterSale(order: any): boolean {
  if (!order) return false;

  const paid = Number(order.paid) || 0;
  const status = String(order.orderStatus || '').toLowerCase();

  // Settled / paid sales must appear in POS sales report & daily closing
  if (paid > 0) return false;
  if (status === 'fulfilled') return false;

  // Only unpaid Pay Later stays hidden from reports
  const payLaterAmount = Number(order?.payLater?.amount) || 0;
  return status === 'paylater' || payLaterAmount > 0;
}

function buildSaleOrderProductItem(raw: Record<string, unknown>): Record<string, unknown> {
  const item: Record<string, unknown> = {};
  const code = raw.code ?? raw.productId;
  if (code != null) item.code = String(code);
  if (raw.name != null) item.name = String(raw.name);
  if (raw.quantity != null) item.quantity = Number(raw.quantity);
  if (raw.unitPrice != null) item.unitPrice = Number(raw.unitPrice);
  if (raw.subTotal != null) item.subTotal = Number(raw.subTotal);
  if (raw.type != null) item.type = String(raw.type);
  if (raw.category != null) item.category = String(raw.category);

  if (Array.isArray(raw.ingredients) && raw.ingredients.length) {
    item.ingredients = raw.ingredients;
  }
  return item;
}

export interface SaleOrderCreateOptions {
  company: string;
  branch: string;
}

/** Body for POST .../branches/:branchId/sale-orders (CreateSaleOrderDto). */
export function buildSaleOrderCreatePayload(
  order: object,
  products: object[],
  options: SaleOrderCreateOptions,
): Record<string, unknown> {
  const o = (order || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    company: options.company,
    branch: options.branch,
    products: (products || []).map((p) => buildSaleOrderProductItem(p as Record<string, unknown>)),
  };

  for (const key of SALE_ORDER_FIELDS) {
    const value = o[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'number') {
      payload[key] = value;
    } else {
      payload[key] = String(value);
    }
  }

  const soldTo = buildSoldTo(o.soldTo);
  if (soldTo) payload.soldTo = soldTo;

  const payLater = buildPayLater(o.payLater);
  if (payLater === true) payload.payLater = true;

  return payload;
}
